import { ThemeProvider } from '@mui/material';
import { deDE, enUS, frFR, Localization } from '@mui/material/locale';
import { createTheme, Theme } from '@mui/material/styles';
import { ThemeProvider as LegacyThemeProvider } from '@mui/styles';
import {
  deDE as dateDeDE,
  enUS as dateEnUS,
  frFR as dateFrFR,
} from '@mui/x-date-pickers/locales';
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import './App.css';
import './components/Print/printStyles.css';

import ForgotPasswordComponent from './components/Account/ForgotPasswordComponent';
import RedeemInviteComponent from './components/Account/RedeemInviteComponent';
import ResetPasswordComponent from './components/Account/ResetPasswordComponent';
import VerifyEmailComponent from './components/Account/VerifyEmailComponent';
import ErrorDialog from './components/ErrorHandling/ErrorDialog';
import LoginComponent from './components/Login/LoginComponent';
import PermissionProvider from './components/Permissions/PermissionProvider';
import { ToastProvider } from './components/ToastList/ToastContext';
import { logout } from './helpers/api/api';
import { useClientTheme } from './helpers/hooks/useClientTheme';
import { Role } from './model/account/role';
import { Temperature } from './model/temperature';
import DashboardPage from './pages/DashboardPage';
import ActivityTrackerDetail from './pages/DashboardPage/ActivityTrackerDetail';
import AddPatientPage from './pages/DashboardPage/AddPatientPage';
import AdminMonitoringPage from './pages/DashboardPage/AdminMonitoringPage';
import AdminPanelPage from './pages/DashboardPage/AdminPanelPage';
import AlertDetail from './pages/DashboardPage/AlertDetail';
import TemperatureDevPage from './pages/DashboardPage/AlertDetail/TemperatureDevPage';
import ComplianceDetail from './pages/DashboardPage/ComplianceDetail';
import AverageWalkingSpeedDetail from './pages/DashboardPage/DailyParameters/AverageWalkingSpeedDetail';
import DailyParametersHomePage from './pages/DashboardPage/DailyParameters/DailyParametersHome';
import PatientDetail from './pages/DashboardPage/PatientDetailPage';
import PatientsPage from './pages/DashboardPage/PatientsPage';
import SettingsPage from './pages/DashboardPage/SettingsPage';
import SupportPage from './pages/DashboardPage/SupportPage';
import TestDetail from './pages/DashboardPage/TestDetail';
import TenMetreWalkDetail from './pages/DashboardPage/TestDetail/TenMetreWalkDetail';
import HomePage from './pages/HomePage';
import NotFound from './pages/NotFound';
import { PrivateRoute } from './PrivateRoute';

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {
    clientId?: string;
    productNames: {
      clientName: string;
      clientCode: string;
      insoles: string;
      dashboard: string;
      app: string;
    };
    images: {
      mainLogo?: any;
    };
    palette: Theme['palette'] & {
      alertLevel1: {
        main: string;
        contrastText: string;
      };
      alertLevel2: {
        main: string;
        contrastText: string;
      };
      alertLevel3: {
        main: string;
        contrastText: string;
      };
    };
    metrics: {
      temperature: {
        unit: Temperature;
        symbol: string;
      };
    };
  }
}

// Maps an app language to the matching MUI core + date-picker locale bundles.
const MUI_LOCALES: Record<string, [Localization, object]> = {
  en: [enUS, dateEnUS],
  de: [deDE, dateDeDE],
  fr: [frFR, dateFrFR],
};

const App: FC = () => {
  const { theme } = useClientTheme();
  const { i18n } = useTranslation();
  const language = i18n.language?.split('-')[0] ?? 'en';

  // Merge the language's MUI locale into the client theme so MUI's built-in
  // component text and date pickers are localised alongside our own strings.
  const localizedTheme = useMemo(() => {
    const [coreLocale, dateLocale] = MUI_LOCALES[language] ?? MUI_LOCALES.en;
    return createTheme(theme, coreLocale, dateLocale);
  }, [theme, language]);

  useEffect(() => {
    document.title = `${theme.productNames.dashboard}: Dashboard`;
  });

  return (
    <LegacyThemeProvider theme={localizedTheme}>
    <ThemeProvider theme={localizedTheme}>
      <PermissionProvider>
        <ToastProvider>
          <div className="App">
            <BrowserRouter basename={import.meta.env.BASE_URL}>
              <Routes>
                <Route path="/" element={<HomePage />}>
                  <Route index element={<LoginComponent />} />
                  <Route
                    path="/invite/redeem"
                    element={<RedeemInviteComponent />}
                  />
                  <Route
                    path="/account/verify-email"
                    element={<VerifyEmailComponent />}
                  />
                  <Route
                    path="forgot-password"
                    element={<ForgotPasswordComponent />}
                  />
                  <Route
                    path="/account/reset-password"
                    element={<ResetPasswordComponent />}
                  />
                </Route>

                <Route
                  path="dashboard"
                  element={
                    <PrivateRoute
                      roles={[Role.User, Role.Admin, Role.SuperAdmin]}
                      component={DashboardPage}
                    />
                  }
                >
                  <Route path="patients" element={<PatientsPage />} />
                  <Route path="patients/add" element={<AddPatientPage />} />
                  <Route
                    path="patients/:patientId"
                    element={<PatientDetail />}
                  />
                  <Route
                    path="patients/:patientId/passive/activitytracker"
                    element={<ActivityTrackerDetail />}
                  />
                  <Route
                    path="patients/:patientId/passive/activitytracker-dev"
                    element={<ActivityTrackerDetail showCharts />}
                  />
                  <Route
                    path="patients/:patientId/passive/:alertType"
                    element={<AlertDetail />}
                  />
                  <Route
                    path="patients/:patientId/passive/temperature-dev"
                    element={<TemperatureDevPage />}
                  />
                  <Route
                    path="patients/:patientId/active/:testType"
                    element={<TestDetail />}
                  />
                  <Route
                    path="patients/:patientId/active/:testType/:testGuid"
                    element={<TenMetreWalkDetail />}
                  />
                  <Route
                    path="patients/:patientId/dailyparameters"
                    element={<DailyParametersHomePage />}
                  />
                  <Route
                    path="patients/:patientId/dailyparameters/AverageWalkingSpeed"
                    element={<AverageWalkingSpeedDetail />}
                  />
                  <Route
                    path="patients/:patientId/other/compliance/"
                    element={<ComplianceDetail />}
                  />
                  <Route
                    path="admin"
                    element={
                      <PrivateRoute
                        roles={[Role.Admin, Role.SuperAdmin]}
                        component={AdminPanelPage}
                      />
                    }
                  />
                  <Route path="support" element={<SupportPage />} />
                  <Route path="monitoring" element={<AdminMonitoringPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
            <ErrorDialog handleClose={() => logout()} />
          </div>
        </ToastProvider>
      </PermissionProvider>
    </ThemeProvider>
    </LegacyThemeProvider>
  );
};

export default App;
